// Cart-page "Add Gift Wrapping" popup - a single shared popup (see
// snippets/cart-gift-wrap-editor.liquid), opened for whichever line's
// gift-wrap-toggle-button was clicked, instead of navigating to the product
// page. Same wrapping-paper carousel/selection/validation as the product
// page's gift-wrap picker (assets/gift-options.js) - only saving differs:
//
// Saving performs the cart mutation directly, mirroring how the product
// page's add-to-cart flow builds the pair (product-form.js's
// addLinkedGiftWrapItem): remove this line, re-add it carrying the gift
// wrap properties merged into whatever other properties it already had
// (engraving included), then add the chosen wrapping-paper variant as its
// own linked line tagged with the same `_Gift Wrap Id`. There's no
// "update properties in place" endpoint for an existing cart line - see
// cart-engraving.js for the same remove-then-add approach used there.


class CartGiftWrapEditor extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-cart-giftwrap-modal]');
    this.overlay = this.querySelector('[data-cart-giftwrap-overlay]');
    this.errorBox = this.modal ? this.modal.querySelector('[data-cart-giftwrap-error]') : null;
    this.toInput = this.modal ? this.modal.querySelector('[data-giftwrap-to]') : null;
    this.messageInput = this.modal ? this.modal.querySelector('[data-giftwrap-message]') : null;
    this.messageErrorBox = this.modal ? this.modal.querySelector('[data-cart-giftwrap-message-error]') : null;
    this.fromInput = this.modal ? this.modal.querySelector('[data-giftwrap-from]') : null;
    this.counter = this.modal ? this.modal.querySelector('[data-giftwrap-counter]') : null;
    this.saveButton = this.modal ? this.modal.querySelector('[data-cart-giftwrap-save]') : null;
    this.swiperContainer = this.modal ? this.modal.querySelector('[data-giftwrap-swiper]') : null;
    this.selectedVariantId = null;
    // The line currently being wrapped: { lineKey, variantId }.
    this.currentLine = null;

    if (!this.modal) return;

    // Query close triggers (the header's [x] button plus the overlay, both
    // marked data-cart-giftwrap-close) before relocating - relocateDrawer
    // moves the modal/overlay out to <body>, so afterwards they're no
    // longer descendants of `this` and this.querySelectorAll would find
    // nothing to attach the close handler to.
    const closeElements = this.querySelectorAll('[data-cart-giftwrap-close]');

    this.relocateDrawer(this.modal, this.overlay);
    this.initSwiper();
    this.attachOptionListeners();

    if (this.messageInput) {
      this.messageInput.addEventListener('input', () => {
        this.showMessageError(null);
        this.updateCounter();
        this.updateSaveState();
      });
    }
    if (this.toInput) this.toInput.addEventListener('input', () => this.updateSaveState());
    if (this.saveButton) this.saveButton.addEventListener('click', () => this.save());

    closeElements.forEach((element) => {
      element.addEventListener('click', () => this.close());
    });

    this.updateCounter();
    this.updateSaveState();
  }

  // Same Swiper setup as the product page's gift-wrap carousel
  // (assets/gift-options.js) - free-dragging slides sized by their own CSS
  // width, reusing the theme's existing .fc-scrollbar styling.
  attachOptionListeners() {
    this.optionButtons = this.modal ? Array.from(this.modal.querySelectorAll('[data-giftwrap-option]')) : [];
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectOption(button));
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          this.selectOption(button);
        }
      });
    });
  }

  initSwiper() {
    if (!this.swiperContainer || this.swiperContainer.swiper) return;

    this.swiper = new Swiper(this.swiperContainer, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      freeMode: true,
      grabCursor: true,
      watchOverflow: true,
      a11y: false,

      mousewheel: {
        forceToAxis: true,
      },

      scrollbar: {
        el: this.swiperContainer.querySelector('.fc-scrollbar'),
        draggable: true,
      },
    });
  }

  findOptionByVariantId(variantId) {
    return this.optionButtons.find((button) => String(button.dataset.variantId) === String(variantId));
  }

  getErrorMessage(type) {
    if (!this.errorBox) return '';
    const map = {
      generic: this.errorBox.dataset.errorGeneric,
      selectPaper: this.errorBox.dataset.errorSelectPaper,
      messageRequired: this.errorBox.dataset.errorMessageRequired,
    };
    return map[type] || '';
  }

  showError(message) {
    if (!this.errorBox) return;
    if (!message) {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
      return;
    }
    this.errorBox.textContent = message;
    this.errorBox.hidden = false;
  }

  showMessageError(message) {
    if (!this.messageErrorBox) return;

    if (!message) {
      this.messageErrorBox.hidden = true;
      this.messageErrorBox.textContent = '';
      return;
    }

    this.messageErrorBox.textContent = message;
    this.messageErrorBox.hidden = false;
  }

  // Checkbox behavior: clicking the already-selected paper unchecks it
  // (clears the selection) instead of leaving it stuck selected the way a
  // radio button would. Clicking a different paper still swaps the
  // selection to it, since only one wrapping paper applies at a time.
  selectOption(button) {
    const isAlreadySelected = button.dataset.variantId === this.selectedVariantId;
    this.selectedVariantId = isAlreadySelected ? null : button.dataset.variantId;
    this.optionButtons.forEach((optionButton) => {
      const isSelected = !isAlreadySelected && optionButton === button;
      optionButton.classList.toggle('is-selected', isSelected);
      optionButton.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
    this.showError(null);
    this.updateSaveState();
  }

  updateCounter() {
    if (!this.messageInput || !this.counter) return;
    const max = this.messageInput.maxLength > 0 ? this.messageInput.maxLength : 200;
    const remaining = Math.max(max - this.messageInput.value.length, 0);
    if (!this.counter.dataset.template) {
      this.counter.dataset.template = this.counter.textContent;
    }
    this.counter.textContent = this.counter.dataset.template.replace(/\d+/, remaining);
  }

  updateSaveState() {
    if (!this.saveButton) return;

    this.saveButton.disabled = false;
  }

  // Called by GiftWrapToggleButton for a fresh line (just { lineKey,
  // variantId }, opens to a blank form) and by GiftWrapSummary's Edit
  // button for an already-wrapped line (also carries giftWrapId/paper/
  // price/to/message/from, read from that line's own saved properties) -
  // same prefill-from-saved-values approach as the product page's
  // openGiftWrapModal, just sourced from data-* attributes instead of
  // hidden inputs since there's no add-to-cart form here.
  openForLine(line, focusElement) {
    if (!this.modal) return;
    this.currentLine = line;
    this.showError(null);

    const template = document.querySelector(`template[data-cart-giftwrap-template="${line.lineKey}"]`);
    if (template && this.swiperContainer) {
      const optionsContainer = this.swiperContainer.querySelector('[data-giftwrap-options]');
      if (optionsContainer) {
        optionsContainer.innerHTML = template.innerHTML;
        this.attachOptionListeners();
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
        this.initSwiper();
      }
    }

    if (this.toInput) this.toInput.value = line.to || '';
    if (this.messageInput) this.messageInput.value = line.message || '';
    if (this.fromInput) this.fromInput.value = line.from || '';

    const savedPaperTitle = line.paper || '';
    const matchingOption = savedPaperTitle
      ? this.optionButtons.find((button) => button.dataset.productTitle === savedPaperTitle)
      : null;

    if (matchingOption) {
      // Reset first so selectOption's toggle check (which unchecks an
      // already-selected paper) always treats this as a fresh selection
      // rather than a re-click that would uncheck it.
      this.selectedVariantId = null;
      this.selectOption(matchingOption);
    } else {
      this.selectedVariantId = null;
      this.optionButtons.forEach((button) => {
        button.classList.remove('is-selected');
        button.setAttribute('aria-checked', 'false');
      });
    }

    this.updateCounter();
    this.updateSaveState();
    this.modal.show(focusElement || this.optionButtons[0], this.optionButtons[0]);

    // Same re-measure-on-open as the product page's carousel - the drawer is
    // off-screen but still in layout when Swiper first measured slide
    // widths, so cached sizes should be correct, but re-measure anyway since
    // it's cheap and guards against a stale viewport/layout pass.
    if (this.swiper) {
      requestAnimationFrame(() => this.swiper.update());
    }
  }

  close() {
    if (this.modal) this.modal.hide();
  }

  save() {
    if (!this.currentLine) return;

    this.showError(null);
    this.showMessageError(null);

    const paperNotSelected = !this.selectedVariantId;

    const message = this.messageInput
      ? this.messageInput.value.trim()
      : '';

    const messageMissing = !message;

    if (paperNotSelected) {
      this.showError(this.getErrorMessage('selectPaper'));
    }

    if (messageMissing) {
      this.showMessageError(this.getErrorMessage('messageRequired'));
    }

    if (paperNotSelected || messageMissing) {
      if (paperNotSelected) {
        this.optionButtons[0]?.focus();
      } else if (messageMissing && this.messageInput) {
        this.messageInput.focus();
      }

      return;
    }

    const selectedOption = this.findOptionByVariantId(this.selectedVariantId);
    const paperTitle = selectedOption ? selectedOption.dataset.productTitle : '';
    const priceText = selectedOption ? selectedOption.dataset.priceText : '';
    const to = this.toInput ? this.toInput.value.trim() : '';
    const from = this.fromInput ? this.fromInput.value.trim() : '';
    const wrapVariantId = this.selectedVariantId;
    // Reuse the existing pair id when editing an already-saved selection
    // (keeps the linked wrap line's pairing consistent across the edit) -
    // same as the product page's saveGiftWrap(); only mint a fresh one the
    // first time this line is wrapped.
    const pairId = this.currentLine.giftWrapId || `giftwrap-${Date.now()}`;

    this.setButtonLoading(this.saveButton, true);

    const { lineKey, variantId } = this.currentLine;

    fetch(`${routes.cart_url}.json`)
      .then((response) => response.json())
      .then((cart) => {
        const item = cart.items.find((cartItem) => cartItem.key === lineKey);
        if (!item) return Promise.reject(new Error('Line not found'));

        // Editing an existing selection: the previously linked wrap-paper
        // line (possibly a different variant than the one just chosen)
        // can't be updated in place any more than the main line's
        // properties can, so it has to go before the new one is added.
        const existingLinkedItem = cart.items.find(
          (cartItem) =>
            cartItem.key !== lineKey &&
            cartItem.properties &&
            cartItem.properties['_Gift Wrap Id'] === pairId &&
            cartItem.properties['_Gift Wrap Item'] === 'true'
        );

        const existingEngravingFeeItem = cart.items.find(
          (cartItem) =>
            cartItem.key !== lineKey &&
            cartItem.properties &&
            cartItem.properties['_Linked Engraving Id'] === item.properties['_Engraving Id'] &&
            cartItem.properties['_type'] === 'engraving-fee'
        );

        // Keep every other property this line already had (engraving
        // included) and only add/replace the gift wrap ones - same merge
        // approach cart-engraving.js's save() uses for the reverse case.
        // To/From explicitly delete rather than skip when blank, since an
        // edit may be clearing a value the line previously had.
        const properties = { ...item.properties };
        properties['Gift Wrap Paper'] = paperTitle;
        properties['Gift Wrap Price'] = priceText;
        if (to) properties['Gift Wrap To'] = to;
        else delete properties['Gift Wrap To'];
        properties['Gift Wrap Message'] = message;
        if (from) properties['Gift Wrap From'] = from;
        else delete properties['Gift Wrap From'];
        properties['_Gift Wrap Id'] = pairId;

        // Keep Bloomreach line JSON in sync so checkout/purchase see gift_wrapping: true.
        try {
          const bloomRaw = properties._bloomreach_product_json;
          if (bloomRaw) {
            const bloom = typeof bloomRaw === 'object' ? { ...bloomRaw } : JSON.parse(bloomRaw);
            bloom.gift_wrapping = true;
            properties._bloomreach_product_json = JSON.stringify(bloom);
          }
        } catch (error) {}

        const removeUpdates = { [lineKey]: 0 };
        if (existingLinkedItem) removeUpdates[existingLinkedItem.key] = 0;
        if (existingEngravingFeeItem) removeUpdates[existingEngravingFeeItem.key] = 0;

        return fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ updates: removeUpdates }),
        })
          .then((response) => response.json())
          .then(() =>
            fetch(`${routes.cart_add_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                items: [
                  {
                    id: parseInt(variantId, 10),
                    quantity: item.quantity,
                    properties,
                    selling_plan: item.selling_plan_allocation
                      ? item.selling_plan_allocation.selling_plan.id
                      : undefined,
                  },
                ],
              }),
            })
          )
          .then((response) => response.json())
          .then((addResponse) => {
            if (addResponse.status) {
              return Promise.reject(new Error(addResponse.description || addResponse.message));
            }

            // Extract the newly generated parent line key safely
            const addedVariantId = parseInt(variantId, 10);
            const parentItem = addResponse.items
              ? addResponse.items.find((item) => item.variant_id === addedVariantId || item.id === addedVariantId) || addResponse.items[0]
              : addResponse;
            const parentLineKey = parentItem ? parentItem.key : undefined;

            // Add the wrapping paper as its own cart line, tagged with the
            // same `_Gift Wrap Id` the main line now carries - same pairing
            // product-form.js's addLinkedGiftWrapItem uses on the product
            // page, so the two cascade-remove together later
            // (cart.js's getLinkedGiftWrapLine/removeLinkedLines). Quantity
            // matches the main line's own (current) quantity rather than a
            // hardcoded 1, since it's sold one-per-unit.
            const itemsToAdd = [
              {
                id: parseInt(wrapVariantId, 10),
                quantity: item.quantity,
                parent_line_key: parentLineKey || undefined,
                properties: {
                  '_Gift Wrap Id': pairId,
                  '_Gift Wrap Item': 'true',
                },
              }
            ];

            if (existingEngravingFeeItem) {
              itemsToAdd.push({
                id: existingEngravingFeeItem.variant_id,
                quantity: item.quantity,
                parent_line_key: parentLineKey || undefined,
                properties: existingEngravingFeeItem.properties
              });
            }

            return fetch(`${routes.cart_add_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                items: itemsToAdd,
              }),
            })
              .then((response) => response.json())
              .then((wrapResponse) => {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift-wrap-editor', cartData: wrapResponse });
                this.currentLine = null;
                this.close();
              });
          });
      })
      .catch((error) => {
        console.error(error);
        this.showError(this.getErrorMessage('generic'));
      })
      .finally(() => {
        this.setButtonLoading(this.saveButton, false);
      });
  }

  // Called by GiftWrapSummary's Remove button - strips the gift wrap
  // properties from this line (keeping the product itself and everything
  // else it carries, engraving included) and deletes its linked
  // wrapping-paper line. Same remove-then-add approach as save() above and
  // cart-engraving.js's removeEngravingFromLine, since there's no
  // "update/remove properties in place" endpoint for an existing line.
  removeGiftWrap(lineKey, variantId, triggerButton) {
    this.setButtonLoading(triggerButton, true);

    fetch(`${routes.cart_url}.json`)
      .then((response) => response.json())
      .then((cart) => {
        const item = cart.items.find((cartItem) => cartItem.key === lineKey);
        if (!item) return Promise.reject(new Error('Line not found'));

        const giftWrapId = item.properties && item.properties['_Gift Wrap Id'];
        const linkedItem = giftWrapId
          ? cart.items.find(
              (cartItem) =>
                cartItem.key !== lineKey &&
                cartItem.properties &&
                cartItem.properties['_Gift Wrap Id'] === giftWrapId &&
                cartItem.properties['_Gift Wrap Item'] === 'true'
            )
          : null;

        const existingEngravingFeeItem = cart.items.find(
          (cartItem) =>
            cartItem.key !== lineKey &&
            cartItem.properties &&
            cartItem.properties['_Linked Engraving Id'] === item.properties['_Engraving Id'] &&
            cartItem.properties['_type'] === 'engraving-fee'
        );

        const properties = { ...item.properties };
        ['Gift Wrap Paper', 'Gift Wrap Price', 'Gift Wrap To', 'Gift Wrap Message', 'Gift Wrap From', '_Gift Wrap Id'].forEach(
          (key) => delete properties[key]
        );

        try {
          const bloomRaw = properties._bloomreach_product_json;
          if (bloomRaw) {
            const bloom = typeof bloomRaw === 'object' ? { ...bloomRaw } : JSON.parse(bloomRaw);
            bloom.gift_wrapping = false;
            properties._bloomreach_product_json = JSON.stringify(bloom);
          }
        } catch (error) {}

        const removeUpdates = { [lineKey]: 0 };
        if (linkedItem) removeUpdates[linkedItem.key] = 0;
        if (existingEngravingFeeItem) removeUpdates[existingEngravingFeeItem.key] = 0;

        return fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ updates: removeUpdates }),
        })
          .then((response) => response.json())
          .then(() =>
            fetch(`${routes.cart_add_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                items: [
                  {
                    id: parseInt(variantId, 10),
                    quantity: item.quantity,
                    properties: Object.keys(properties).length ? properties : undefined,
                    selling_plan: item.selling_plan_allocation
                      ? item.selling_plan_allocation.selling_plan.id
                      : undefined,
                  },
                ],
              }),
            })
          )
          .then((response) => response.json())
          .then((addResponse) => {
            if (addResponse.status) {
              return Promise.reject(new Error(addResponse.description || addResponse.message));
            }

            if (!existingEngravingFeeItem) {
              return addResponse;
            }

            const addedVariantId = parseInt(variantId, 10);
            const parentItem = addResponse.items
              ? addResponse.items.find((i) => i.variant_id === addedVariantId || i.id === addedVariantId) || addResponse.items[0]
              : addResponse;
            const parentLineKey = parentItem ? parentItem.key : undefined;

            return fetch(`${routes.cart_add_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                items: [
                  {
                    id: existingEngravingFeeItem.variant_id,
                    quantity: item.quantity,
                    parent_line_key: parentLineKey || undefined,
                    properties: existingEngravingFeeItem.properties
                  }
                ]
              }),
            }).then((res) => res.json());
          });
      })
      .then((response) => {
        if (response.status) {
          console.error(response.description || response.message);
          return;
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift-wrap-editor', cartData: response });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.setButtonLoading(triggerButton, false);
      });
  }

  // Shared by save()'s Save & continue button (already .button-classed, so
  // this just drives the theme's existing .button.loading treatment - see
  // base.css) and the Remove triggers (remove-product-dialog, the gift-wrap
  // summary's Remove), which get the equivalent look via matching CSS
  // scoped to their own selectors in component-cart.css since they aren't
  // .button elements themselves.
  setButtonLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('loading', isLoading);
    button.disabled = isLoading;
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  // Same fixed-position relocation trick as gift-options.js's drawers - see
  // that file for why.
  relocateDrawer(drawer, overlay) {
    if (!drawer || drawer.parentElement === document.body) return;
    document.body.appendChild(drawer);
    if (overlay) drawer.after(overlay);
  }
}

if (!customElements.get('cart-gift-wrap-editor')) {
  customElements.define('cart-gift-wrap-editor', CartGiftWrapEditor);
}

// Thin trigger next to each eligible line (main-cart-items.liquid) - a
// custom element rather than a plain button + delegated listener so newly
// rendered rows after every cart AJAX update pick up their click handler
// again automatically (same reasoning as cart.js's CartRemoveButton/
// EngravingEditButton).
class GiftWrapToggleButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', () => {
      const editor = document.querySelector('cart-gift-wrap-editor');
      if (!editor || typeof editor.openForLine !== 'function') return;

      editor.openForLine(
        { lineKey: this.dataset.lineKey, variantId: this.dataset.variantId },
        this.querySelector('button') || this
      );
    });
  }
}

if (!customElements.get('gift-wrap-toggle-button')) {
  customElements.define('gift-wrap-toggle-button', GiftWrapToggleButton);
}

// "Gift wrapped" summary row shown once a line is wrapped (main-cart-items.liquid,
// same gift-option__summary markup as the product page) - Edit reopens the
// shared drawer prefilled from this row's own data-* attributes (the line's
// saved properties), Remove strips the gift wrap without touching the
// product itself. Its own custom element for the same reason as
// GiftWrapToggleButton above - it has to keep working after every cart AJAX
// re-render, not just on first page load.
class GiftWrapSummary extends HTMLElement {
  connectedCallback() {
    const editTrigger = this.querySelector('[data-cart-giftwrap-edit-trigger]');
    const removeTrigger = this.querySelector('[data-cart-giftwrap-remove-trigger]');

    if (editTrigger) {
      editTrigger.addEventListener('click', () => {
        const editor = document.querySelector('cart-gift-wrap-editor');
        if (!editor || typeof editor.openForLine !== 'function') return;

        editor.openForLine(
          {
            lineKey: this.dataset.lineKey,
            variantId: this.dataset.variantId,
            giftWrapId: this.dataset.giftWrapId,
            paper: this.dataset.giftWrapPaper,
            price: this.dataset.giftWrapPrice,
            to: this.dataset.giftWrapTo,
            message: this.dataset.giftWrapMessage,
            from: this.dataset.giftWrapFrom,
          },
          editTrigger
        );
      });
    }

    if (removeTrigger) {
      removeTrigger.addEventListener('click', () => {
        const editor = document.querySelector('cart-gift-wrap-editor');
        if (!editor || typeof editor.removeGiftWrap !== 'function') return;
        editor.removeGiftWrap(this.dataset.lineKey, this.dataset.variantId, removeTrigger);
      });
    }
  }
}

if (!customElements.get('gift-wrap-summary')) {
  customElements.define('gift-wrap-summary', GiftWrapSummary);
}
